"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Specializations() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const meta = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const archiveItems = [{
    src: "https://gstna.com/wp-content/uploads/2026/07/die-cut-6-scaled.png?v=oig4v2fped",
    srcset: "https://gstna.com/wp-content/uploads/2026/07/die-cut.png?v=bz54d6jxwwr-wvc-srcset",
    sizes: "https://gstna.com/wp-content/uploads/2026/07/die-cut.png?v=345hyq7b6ks-wvc-sizes",
    alt: "High-speed industrial die-cutting system in operation",
    name: "Precision Die-Cutting",
    detail: "HIGH-SPEED SYSTEMS — TOLERANCE CONTROL — 2024",
    colSpan: "md:col-span-4",
    aspect: "aspect-[3/4]",
    offset: "md:mt-0"
  }, {
    src: "https://gstna.com/wp-content/uploads/2026/07/jumbo-roll-slitting-scaled.png?v=mh3w0ki91j",
    srcset: "https://gstna.com/wp-content/uploads/2026/07/slitter.png?v=bnvkhnj1mdk-wvc-srcset",
    sizes: "https://gstna.com/wp-content/uploads/2026/07/slitter.png?v=kkjz331ay2b-wvc-sizes",
    alt: "Precision lathe slitter processing industrial film rolls",
    name: "Lathe Slitting",
    detail: "PRECISION SLITTERS — CLEAN EDGE — 2024",
    colSpan: "md:col-span-4",
    aspect: "aspect-[4/5]",
    offset: "md:mt-30"
  }, {
    src: "https://gstna.com/wp-content/uploads/2026/07/laminating-1-scaled.png?v=t0li4tah7qs",
    srcset: "https://gstna.com/wp-content/uploads/2026/07/laminating-1-scaled.png?v=t0zqjywebqf-wvc-srcset",
    sizes: "https://gstna.com/wp-content/uploads/2026/07/laminating-1-scaled.png?v=awls56o88sl-wvc-sizes",
    alt: "Advanced multi-layer laminator for industrial adhesives",
    name: "Laminating",
    detail: "MULTI-LAYER SYSTEMS — ADHESIVE BONDING — 2023",
    colSpan: "md:col-span-4",
    aspect: "aspect-[3/4]",
    offset: "md:-mt-15"
  }];
  return <section data-section-id="4808" id="material-specializations" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-indigo-muted">
            GST_REF: 44.02 // 2024
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-16 md:mb-24">
          <div className={`md:col-span-7 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h2 className="font-default text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-indigo-primary">
              Engineered
              <br />
              <span className="italic text-indigo-wash">Capabilities.</span>
            </h2>
          </div>

          <div ref={meta.ref} className={`md:col-span-5 md:col-start-8 transition-all duration-1000 delay-200 ease-out ${meta.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default text-lg md:text-xl text-indigo-muted leading-relaxed mb-6">
              Global Standard Technology delivers precision-driven converting services. 
              From die-cutting to multi-layer lamination, our processes are optimized 
              for industrial efficiency and exacting quality standards.
            </p>
            <Link to="/ProductsPage" className="group inline-flex items-center gap-2 font-default italic text-base md:text-lg text-indigo-primary border-b border-indigo-primary hover:border-indigo-wash pb-1 transition-colors duration-300">
              <span className="group-hover:[text-decoration-thickness:2px] [text-decoration:underline] [text-decoration-thickness:1px] underline-offset-4">
                Explore Our Technical Processes
              </span>
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 lg:gap-10">
          {archiveItems.map((item, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <figure data-index={i} key={i} ref={reveal.ref} className={`group ${item.colSpan} ${item.offset} transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <div className={`overflow-hidden rounded-lg shadow-md ${item.aspect} bg-indigo-tint`} style={{
              clipPath: reveal.isVisible ? "inset(0% 0% 0% 0%)" : "inset(0% 0% 100% 0%)",
              transition: "clip-path 1100ms cubic-bezier(0.65, 0, 0.35, 1)"
            }}>
                  <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03] group-hover:-translate-y-1" />
                </div>

                <figcaption className={`mt-5 transition-all duration-700 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
              transitionDelay: `${i * 120 + 1100}ms`
            }}>
                  <p className="font-default italic text-2xl md:text-3xl text-indigo-primary leading-tight">
                    <span className="border-b border-transparent group-hover:border-indigo-wash [border-bottom-width:1px] group-hover:[border-bottom-width:2px] transition-all duration-300">
                      &ldquo;{item.name}&rdquo;
                    </span>
                  </p>
                  <p className="mt-2 font-mono text-[0.7rem] md:text-xs tracking-[0.18em] uppercase text-indigo-muted">
                    {item.detail}
                  </p>
                </figcaption>
              </figure>;
        })}
        </div>
      </div>
    </section>;
}